import { ToolTip, SpinnerLoader } from '@/components';
import { ECOMMERCE } from '@/constants/systemRoutes';
import { useTables, useClient, useNetworkStatusContext } from '@/hooks';
import { ClientProps } from '@/types';
import { useCallback, useEffect, useState } from 'react';
import * as S from './styles';
import { setOnStorage } from '@/shared';
import { useDownloadFiles } from '@/talons';
import { useNavigate } from 'react-router-dom';

interface Props {
  selectedClient: ClientProps | undefined;
  cbFunction?: () => void;
  /** se true (default), força reload da página após navegar */
  forceReload?: boolean;
}

const ModalListCompanies = ({ selectedClient, cbFunction, forceReload = true }: Props) => {
  const { tables } = useTables();
  const { connectivityStatus } = useNetworkStatusContext();
  const isOnline = connectivityStatus === 'online';

  const navigate = useNavigate();
  const { setSelectedClient } = useClient();
  const { updateStock } = useDownloadFiles();

  const [showTooltip, setShowTooltip] = useState(false);
  const [loading, setLoading] = useState(false);
  const [missingDeps, setMissingDeps] = useState<Array<{ name: string; isDownloaded: boolean }>>([]);

  const handleDisabled = useCallback(() => {
    const dependenciesMap = {
      'Produtos': tables.current.produtos,
      'Categorias': tables.current.categoria,
      'Estoque': tables.current.estoque,
      'Marcas': tables.current.marcas,
      'Percentual Fornecedor': tables.current.percentual_fornecedor,
      'Preços': tables.current.preco,
      'Substituição Tributária': tables.current.cod_regime_tributario,
    };

    const statusDependencies = Object.entries(dependenciesMap).map(([key, value]) => ({
      name: key,
      isDownloaded: !!value?.length,
    }));

    setShowTooltip(statusDependencies.some((dep) => !dep.isDownloaded));
    setMissingDeps(statusDependencies);
  }, [tables]);

  useEffect(() => {
    handleDisabled();
  }, [handleDisabled]);

  const goTo = useCallback(async () => {
    if (!selectedClient) return;
  
    setLoading(true);
    try {
      // 1) Persistir cliente
      setOnStorage('cart_id', selectedClient);
      setOnStorage('selected_client', selectedClient);
      setSelectedClient(selectedClient);
  
      // 2) Atualizar/verificar estoque (aguardando terminar SEMPRE)
      if (isOnline) {
        try {
          await updateStock(); // <- só sai daqui quando terminar (com sucesso ou sem updates)
        } catch (err) {
          console.error('Falha ao atualizar estoque:', err);
        }
      }
  
      navigate(ECOMMERCE.link, { replace: true });
      // setTimeout(() => {
      //   window.location.reload();
      // }, 0);
  
      cbFunction && cbFunction();
    } finally {
      setLoading(false);
    }
  }, [selectedClient, setSelectedClient, isOnline, updateStock, navigate, cbFunction]);
  

  return (
    <S.BottomContent>
      <ToolTip
        direction="top"
        show={showTooltip}
        displayMode="default"
        text={
          <>
            <div style={{ display: 'flex', flexDirection: 'column', paddingBottom: '5px' }}>
              <p>Por favor, sincronize para liberar o acesso:</p>
              <ul style={{ marginTop: '8px', paddingLeft: '10px' }}>
                {missingDeps.map((dep) => (
                  <li style={{ textAlign: 'left', fontSize: '10px', color: '#ccc' }} key={dep.name}>
                    {dep.name}{' '}
                    {dep.isDownloaded ? (
                      <span style={{ color: '#00aa00' }}>✓</span>
                    ) : (
                      <span style={{ color: '#ff0000' }}>✗</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </>
        }
      >
        <S.ButtonAccess
          onClick={goTo}
          disabled={showTooltip || loading || !selectedClient}
          aria-busy={loading}
          data-test="modal-companies-button-go-in"
        >
          <S.LoadBox>
            Entrar com esse cliente {loading && <SpinnerLoader />}
          </S.LoadBox>
        </S.ButtonAccess>
      </ToolTip>
    </S.BottomContent>
  );
};

export default ModalListCompanies;
